/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useDivisionMetrics Hook
 *
 * Fetches operational metrics for a division: revenue, costs, jobs,
 * labor hours, and monthly trends.
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

// ============================================================================
// TYPES
// ============================================================================

export interface DivisionMetricsSummary {
  revenue: number;
  activeJobs: number;
  totalCosts: number;
  profitMargin: number;
  crewCount: number;
  avgHourlyRate: number;
  totalLaborHours: number;
}

export interface MonthlyTrendEntry {
  month: string; // YYYY-MM
  revenue: number;
  costs: Record<string, number>; // by cost_type
  totalCosts: number;
}

export interface DivisionMetricsData {
  summary: DivisionMetricsSummary;
  monthlyTrend: MonthlyTrendEntry[];
}

// ============================================================================
// HOOK
// ============================================================================

export function useDivisionMetrics(divisionId: string | null) {
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled, isAuthReady } =
    useOrganizationQueryGuard({
      enabled: !!divisionId,
      debugKey: "useDivisionMetrics",
    });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const metricsQuery = useQuery<DivisionMetricsData>({
    queryKey: ["division-metrics", organizationId, divisionId],
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    meta: { entity: "division-metrics" },
    queryFn: async () => {
      const res = await api.get<DivisionMetricsData>(
        `/api/divisions/${divisionId}/metrics`,
      );

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      return res.data as DivisionMetricsData;
    },
  });

  const emptyMetrics: DivisionMetricsData = {
    summary: {
      revenue: 0,
      activeJobs: 0,
      totalCosts: 0,
      profitMargin: 0,
      crewCount: 0,
      avgHourlyRate: 0,
      totalLaborHours: 0,
    },
    monthlyTrend: [],
  };

  return {
    metrics: metricsQuery.data ?? emptyMetrics,
    isLoading:
      !!divisionId &&
      (!isAuthReady ||
        metricsQuery.isLoading ||
        (!metricsQuery.data && !metricsQuery.error)),
    error: metricsQuery.error?.message ?? null,
    refetch: metricsQuery.refetch,
  };
}
